import { inject, Injectable } from '@angular/core';
import { RATE_LIMIT_BYPASS_ENABLED_KEY } from '@app/api/interceptors/rate-limit-bypass.interceptor';
import { UserConfigService } from '@sb/shared/user-config';
import { environment } from 'src/environments/environment';

/**
 * The API's FrontendRateLimiter (core repo) token-buckets non-GET requests per
 * (user, path): 10 per 5s AND 30 per 60s. Mirror both buckets client-side with
 * slightly smaller capacities as headroom for requests fired elsewhere in the app.
 */
const RATE_LIMIT_BUCKETS = [
  { capacity: 8, refillPerSecond: 10 / 5 },
  { capacity: 26, refillPerSecond: 30 / 60 },
];

interface TokenBucketState {
  tokens: number;
  lastRefillMs: number;
  refillPerSecond: number;
  capacity: number;
}

/**
 * Client-side mirror of the backend rate limiter, tracking token buckets per
 * API path — the same algorithm and keying the backend uses. Provided once per
 * dev tools panel so scaffolding and hydration share the same buckets.
 */
@Injectable()
export class DevToolsRateLimiter {
  #userConfig = inject(UserConfigService);

  #bypassEnabled = this.#userConfig.get(RATE_LIMIT_BYPASS_ENABLED_KEY, false);

  #bucketsByPath = new Map<string, TokenBucketState[]>();

  /**
   * Waits until both token buckets for the path have a token available, then
   * consumes one from each. Skipped entirely when the server-side rate-limit
   * bypass (same toggle + secret as RateLimitBypassInterceptor) is active —
   * the server isn't throttling those requests, so self-throttling them here
   * only slows Dev Tools down for nothing.
   */
  async waitForSlot(apiPath: string): Promise<void> {
    if (this.#bypassEnabled() && environment.rateLimitBypassSecret) {
      return;
    }

    const buckets = this.#bucketsForPath(apiPath);

    let waitMs = this.#refillAndGetWaitMs(buckets);

    while (waitMs > 0) {
      await this.#sleep(waitMs + 50);
      waitMs = this.#refillAndGetWaitMs(buckets);
    }

    buckets.forEach((bucket) => {
      bucket.tokens -= 1;
    });
  }

  #bucketsForPath(apiPath: string): TokenBucketState[] {
    const existingBuckets = this.#bucketsByPath.get(apiPath);

    if (existingBuckets) {
      return existingBuckets;
    }

    const buckets = RATE_LIMIT_BUCKETS.map(({ capacity, refillPerSecond }) => ({
      tokens: capacity,
      lastRefillMs: Date.now(),
      refillPerSecond,
      capacity,
    }));

    this.#bucketsByPath.set(apiPath, buckets);
    return buckets;
  }

  /**
   * Refills the buckets based on elapsed time and returns how long to wait
   * until every bucket has at least one token (0 when a request can be fired).
   */
  #refillAndGetWaitMs(buckets: TokenBucketState[]): number {
    const now = Date.now();

    return buckets.reduce((longestWaitMs, bucket) => {
      const elapsedSeconds = (now - bucket.lastRefillMs) / 1000;

      bucket.tokens = Math.min(bucket.capacity, bucket.tokens + elapsedSeconds * bucket.refillPerSecond);
      bucket.lastRefillMs = now;

      const missingTokens = Math.max(0, 1 - bucket.tokens);
      const bucketWaitMs = (missingTokens / bucket.refillPerSecond) * 1000;

      return Math.max(longestWaitMs, bucketWaitMs);
    }, 0);
  }

  #sleep(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }
}
